module Client.Components.Graph.SoilSensor

open Client.Components.Graph.Recharts
open Client.Components.GraphCommon
open Client.Domain
open Feliz.Recharts
open Fulma
open Shared
open Shared.Dto.MapSensorData
open Shared.Dto.SensorGraphData
open Shared.Infrastructure


type SoilSensorGraphNode = {
    Ticks: double
    Temperature: float
    SoilHumidity: float
    SoilConductivity: float
}

type SoilSensorGraphData = {
    TemperatureAxisTicks: float list
    HumidityAxisTicks: float list
    ConductivityAxisTicks: float list
    Data: SoilSensorGraphNode list
    WetLimit: float option
    DryLimit: float option
}

let createSoilSensorGraphData (wetLimit: float option) (dryLimit: float option) (data: SoilSensorGraphNode list) =
    let temperatureAxis =
        AxisTicks.generateByData true 5. (fun node -> node.Temperature) data

    let humidityAxis =
        AxisTicks.generateByDataWithMinMax true 5. (fun node -> node.SoilHumidity) dryLimit wetLimit data

    let conductivityAxis =
        AxisTicks.generateByData true 100. (fun node -> node.SoilConductivity) data

    {
        TemperatureAxisTicks = temperatureAxis
        HumidityAxisTicks = humidityAxis
        ConductivityAxisTicks = conductivityAxis
        WetLimit = wetLimit
        DryLimit = dryLimit
        Data = data
    }

let toSoilSensorGraphData (data: GraphSoilDataNodeDto) = {
    Ticks = data.TimeStamp.ToUnixTimeSeconds() |> double
    Temperature = data.SoilTemperature
    SoilHumidity = data.SoilHumidity
    SoilConductivity = data.SoilConductivity
}

let mapDataToSoil (data: GraphDataDto) =
    match data with
    | Soil soilData -> Some soilData
    | _ -> None

let graphDataDtoToSoilGraphData (data: GraphSoilDataDto) : SoilSensorGraphData =
    let list = data.Nodes |> List.map toSoilSensorGraphData

    createSoilSensorGraphData data.WetLimit data.DryLimit list

let currentSoilDataBox (data: SoilSensorData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toShortString data.Date.LocalDateTime)
        currentDataLevelItem "Akt. Bodenfeuchtigkeit" (MeasuredValueString.percentageToString data.SoilMoisture)
        currentDataLevelItem "Akt. Temperatur" (MeasuredValueString.temperatureToString data.SoilTemperature)
        currentDataLevelItem
            "Min. Bodenfeuchtigkeit"
            (DatedValue.toString MeasuredValueString.percentageToString data.MinSoilMoisture)
        currentDataLevelItem
            "Max. Bodenfeuchtigkeit"
            (DatedValue.toString MeasuredValueString.percentageToString data.MaxSoilHumidity)
    ]
]

let soilDataGraphs (graphData: SoilSensorGraphData) =
    let xStart = graphData.Data |> List.map (fun node -> node.Ticks) |> List.min

    let xDomain = xAxis.domain (domain.min, domain.max)

    let yDomain = yAxis.domain (domain.min, domain.max)

    let xAxisTickInterval = xAxis.interval.preserveStartEnd

    let dataKey = (fun (p: SoilSensorGraphNode) -> p.Ticks) |> xAxis.dataKey

    let humidityAxisTicks = AxisTicks.createYProperty graphData.HumidityAxisTicks

    let limitAreas = [
        match graphData.DryLimit with
        | Some dryLimit ->
            Recharts.referenceArea [
                referenceArea.fillOpacity 0.3
                referenceArea.x1 xStart
                referenceArea.y1 0.
                referenceArea.y2 dryLimit
                referenceArea.label "Trocken"
            ]
        | None -> ()

        match graphData.WetLimit with
        | Some wetLimit ->
            Recharts.referenceArea [
                referenceArea.fillOpacity 0.3
                referenceArea.x1 xStart
                referenceArea.y1 wetLimit
                referenceArea.label "Nass"
            ]
        | None -> ()
    ]

    let humidityChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                ]
                Recharts.yAxis [
                    yAxis.unit "%"
                    yAxis.number
                    humidityAxisTicks
                    yAxis.tickCount 6
                    yDomain
                ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContent ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilHumidity)
                    line.name "Bodenfeuchtigkeit [%]"
                    line.stroke "#01B0F1"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]

                yield! limitAreas
            ]
        ]
        |> fullHeightGraphBox "Bodenfeuchtigkeit"

    let temperatureAxisTicks = AxisTicks.createYProperty graphData.TemperatureAxisTicks

    let temperatureChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                ]
                Recharts.yAxis [
                    yAxis.unit " °C"
                    temperatureAxisTicks
                    yDomain
                ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContent ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.Temperature)
                    line.name "Bodentemperatur [in °C]"
                    line.stroke "#FFC000"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
            ]
        ]
        |> fullHeightGraphBox "Verlauf der Bodentemperatur"

    let conductivityAxisTicks =
        AxisTicks.createYProperty graphData.ConductivityAxisTicks

    let conductivityChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                ]
                Recharts.yAxis [
                    yAxis.unit " μS/cm"
                    conductivityAxisTicks
                    yDomain
                ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContent ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilConductivity)
                    line.name "Leitfähigkeit in [in μS/cm]"
                    line.stroke "#EB9934"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
            ]
        ]
        |> fullHeightGraphBox "Verlauf der Bodenleitfähigkeit"

    [
        humidityChart
        temperatureChart
        conductivityChart
    ]